import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Flex, Icon, Image, Text, Link } from "@chakra-ui/react";
import { format as formatDate, parseISO as parseISODate } from "date-fns";
import { ReactComponent as GmbIcon } from "../../../assets/icons/gmb-page.svg";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as SocialProviders from "../../../_constants/SocialProvider";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import { formatPostMessage } from "../../../_utils/postMessageFormatter";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";

const SocialPostCardGoogle = ({ account, post, time, postMenu, ...props }) => {
  const { t } = useTranslation(["planner"]);
  const type = account?.social_type;

  const additionalFields = useMemo(() => {
    const additional = post?.additional || [];

    return additional.find(
      (item) =>
        item.source === SocialProviders.GOOGLE &&
        item.type === PostAdditionalTypes.POST
    );
  }, [post]);
  const startDate = useMemo(
    () =>
      additionalFields?.startDate
        ? parseISODate(additionalFields.startDate)
        : null,
    [additionalFields?.startDate]
  );
  const endDate = useMemo(
    () =>
      additionalFields?.endDate ? parseISODate(additionalFields.endDate) : null,
    [additionalFields?.endDate]
  );

  if (!account || !post || !type) {
    return null;
  }

  const renderHeader = () => (
    <Box>
      <Flex sx={{ alignItems: "center", padding: "16px 16px 0" }}>
        <Box
          sx={{
            position: "relative",
            width: "40px",
            height: "40px",
            lineHeight: "40px",
          }}
        >
          <SocialAccountAvatar
            sx={{
              width: "full",
              height: "full",
              fontSize: "16px",
              borderRadius: "full",
            }}
            name={account.name}
            src={account.image}
            socialType={account.social_type}
          >
            <Icon
              as={GmbIcon}
              sx={{
                position: "absolute",
                bottom: "0",
                right: "-9px",
                fontSize: "18px",
                color: "#1a73e8",
                fill: "currentColor",
              }}
            />
          </SocialAccountAvatar>
        </Box>
        <Box sx={{ flex: "1 1 0%", margin: "0 16px" }}>
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
              color: "#202124",
            }}
          >
            <Text noOfLines="1">{account.name}</Text>
          </Box>
          <Flex
            sx={{
              fontSize: "12px",
              fontWeight: "400",
              lineHeight: "16px",
              color: "rgba(0,0,0,0.54)",
            }}
          >
            <Box>{time}</Box>
          </Flex>
        </Box>
        <Box sx={{ marginLeft: "auto" }}>
          <Box>
            {postMenu ? (
              postMenu
            ) : (
              <Icon
                sx={{
                  fontSize: "24px",
                  color: "#757575",
                  fill: "currentColor",
                }}
                viewBox="0 0 24 24"
              >
                <path
                  // eslint-disable-next-line max-len
                  d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
                  fill="currentColor"
                />
              </Icon>
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  );

  const renderFooter = () => {
    const actionStyles = {
      padding: "6px",
      verticalAlign: "middle",
    };
    const actionIconStyles = {
      padding: "6px",
      fontSize: "24px",
      color: "#757575",
      boxSizing: "content-box",
      fill: "currentColor",
    };

    return (
      <Box>
        <Box sx={{ padding: "10px" }}>
          {additionalFields?.topicType === "OFFER" ? (
            <Box>
              {additionalFields?.couponCode ? (
                <Box
                  sx={{
                    backgroundColor: "#fafafa",
                    border: "2px dashed #eeeeee",
                    borderRadius: "6px",
                    margin: "12px 16px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      color: "#616161",
                      fontSize: "14px",
                      letterSpacing: ".06px",
                      marginBottom: "5px",
                    }}
                  >
                    {t("post_preview.google.show_code_at_store")}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "24px",
                      letterSpacing: ".12px",
                      color: "#000",
                    }}
                  >
                    {additionalFields.couponCode}
                  </Box>
                  {additionalFields?.startDate && additionalFields?.endDate ? (
                    <Box
                      sx={{
                        color: "rgba(0,0,0,0.87)",
                        fontSize: "11px",
                        fontWeight: "700",
                        marginTop: "4px",
                      }}
                    >
                      {`${t("post_preview.google.valid")} `}
                      {/* eslint-disable-next-line max-len */}
                      {`${formatDate(
                        startDate,
                        "PPp",
                        dateFnsOptions()
                      ).replace(/[, ]+[0-9]{4}/, "")} - ${formatDate(
                        endDate,
                        "PPp",
                        dateFnsOptions()
                      ).replace(/[, ]+[0-9]{4}/, "")}`}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              {additionalFields?.redeemOnlineUrl ? (
                <Link
                  sx={{
                    display: "block",
                    fontSize: "14px",
                    lineHeight: "24px",
                    color: "#3b78e7",
                    textDecoration: "none",
                    margin: "12px 16px",
                  }}
                  href={additionalFields.redeemOnlineUrl}
                  isExternal
                >
                  {t("post_preview.google.redeem_online")}
                </Link>
              ) : null}
              {additionalFields?.termsConditions ? (
                <Box
                  sx={{
                    borderTop: "1px solid #eeeeee",
                    padding: "12px 16px",
                    color: "#bdbdbd",
                    fontSize: "11px",
                    lineHeight: "14px",
                  }}
                >
                  {additionalFields.termsConditions}
                </Box>
              ) : null}
            </Box>
          ) : null}
          <Flex sx={{ alignItems: "center" }}>
            {additionalFields?.callToActionType &&
            additionalFields?.callToActionUrl ? (
              <Box>
                <Link
                  sx={{
                    fontSize: "14px",
                    lineHeight: "24px",
                    color: "#1a0dab",
                    textDecoration: "none",
                    margin: "6px 16px 6px 6px",
                  }}
                  href={additionalFields.callToActionUrl}
                  isExternal
                >
                  {t(
                    `post_editor.additional_fields.google.post.callToActionType.${additionalFields.callToActionType}`
                  )}
                </Link>
              </Box>
            ) : null}
            <Box sx={{ ...actionStyles, marginLeft: "auto" }}>
              <Icon sx={actionIconStyles} viewBox="0 0 24 24">
                <path
                  // eslint-disable-next-line max-len
                  d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"
                  fill="currentColor"
                />
              </Icon>
            </Box>
          </Flex>
        </Box>
      </Box>
    );
  };

  const renderMessage = () => (
    <Box sx={{ padding: "10px 10px 0" }}>
      {additionalFields?.topicType === "EVENT" ||
      additionalFields?.topicType === "OFFER" ? (
        <>
          <Flex alignItems="flex-start">
            <Box
              sx={{
                flex: "1",
                fontSize: "24px",
                lineHeight: "28px",
                color: "#202124",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
              }}
            >
              {additionalFields?.title || ""}
            </Box>
            {additionalFields?.topicType === "OFFER" ? (
              <Box sx={{ marginLeft: "5px" }}>
                <Icon
                  sx={{
                    fontSize: "24px",
                    color: "#f4b400",
                    fill: "currentColor",
                  }}
                  viewBox="0 0 24 24"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"
                    fill="currentColor"
                  />
                </Icon>
              </Box>
            ) : null}
          </Flex>
          {additionalFields?.startDate && additionalFields?.endDate ? (
            <Box
              sx={{
                fontSize: "14px",
                lineHeight: "24px",
                color: "#202124",
                marginBottom: "12px",
              }}
            >
              {additionalFields?.topicType === "OFFER"
                ? `${t("post_preview.google.valid")} `
                : null}
              {/* eslint-disable-next-line max-len */}
              {`${formatDate(startDate, "PPp", dateFnsOptions()).replace(
                /[, ]+[0-9]{4}/,
                ""
              )} - ${formatDate(endDate, "PPp", dateFnsOptions()).replace(
                /[, ]+[0-9]{4}/,
                ""
              )}`}
            </Box>
          ) : null}
        </>
      ) : null}
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: "400",
          lineHeight: "24px",
          color: additionalFields?.title ? "rgba(0,0,0,0.54)" : "#202124",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          _empty: {
            display: "none",
          },
          "& a": {
            color: "#1a0dab",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: formatPostMessage(post.message),
        }}
      />
    </Box>
  );

  const renderMedia = () => {
    const maxItems = 1;
    const mediaItems = getFixedPhotosVideosOrder(
      post?.photos,
      post?.videos
    ).items;
    const mediaItemsSliced = mediaItems.slice(0, maxItems);

    const mediaItemStyles = {
      display: "block",
      maxWidth: "100%",
      maxHeight: "305px",
      margin: "0 auto",
    };

    return (
      <Box>
        <Box
          sx={{
            marginTop: "10px",
            background: "#000000",
          }}
        >
          {mediaItemsSliced.map((item) => (
            <Box key={item.data.url}>
              <Box>
                {item.field === "videos" ? (
                  <Box
                    as="video"
                    sx={mediaItemStyles}
                    src={item.data.url}
                    controls
                    preload="metadata"
                  />
                ) : (
                  <Image sx={mediaItemStyles} src={item.data.url} />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const showMedia = !!post?.videos?.length || !!post?.photos?.length;

  return (
    <Box
      sx={{
        background: "white",
        fontFamily: "Roboto,HelveticaNeue,Arial,sans-serif",
        boxShadow:
          "0px 0px 5px 0px rgb(0 0 0 / 15%), 0px 5px 5px 0px rgb(0 0 0 / 10%)",
        borderRadius: "8px",
        maxWidth: "645px",
      }}
      {...props}
    >
      <Box>
        {renderHeader()}
        <Box>
          <Box>
            {showMedia ? renderMedia() : null}
            {renderMessage()}
          </Box>
        </Box>
        {renderFooter()}
      </Box>
    </Box>
  );
};

SocialPostCardGoogle.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  postMenu: PropTypes.node,
  time: PropTypes.string.isRequired,
};

SocialPostCardGoogle.defaultProps = {
  postMenu: undefined,
};

export default SocialPostCardGoogle;
